import { useState } from 'react';
import { Select } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useSearchControllerRun } from '@werkbank/shared';

/** Treffer der Mietvertragsnummern-Suche (Kategorie `leases` von /search). */
export interface LeaseHit {
  unitId: string;
  leaseNumber: string;
  unitLabel: string;
  propertyAddress: string;
  customerId: string;
  customerName: string;
  /** Am Mietverhältnis hinterlegte Mieterdaten (Kundentest 2026-08). */
  tenantName: string | null;
  tenantPhone: string | null;
  tenantEmail: string | null;
}

/**
 * MV-Schnellanlage (Tobias-Fluss): Mietvertragsnummer ODER Mietername eintippen
 * (Tobias 2026-08-28 — der Name ist am Telefon oft bekannt, die Nummer nicht)
 * → Einheit, Gebäude und Eigentümer (Rechnungsempfänger, z. B. GWH) werden
 * aufgelöst und an den Dialog zurückgegeben, der damit seine Felder vorbefüllt. Nutzt die
 * `leases`-Kategorie der globalen Suche (P5-A §3) — kein eigener Endpunkt.
 * Der Sage-Import (Tag `sage-kunden`) hat die 4.302 Einheiten dafür geladen.
 */
/**
 * Anzeigetext eines Treffers: Nummer, Mieter, Einheit/Adresse, Eigentümer.
 * Der Mietername steht bewusst vorn — wer nach ihm sucht, muss ihn in der
 * Trefferliste auch wiederfinden.
 */
function beschriftung(l: LeaseHit): string {
  const ort = [l.unitLabel, l.propertyAddress].filter(Boolean).join(', ');
  const mieter = l.tenantName?.trim();
  return `${l.leaseNumber}${mieter ? ` — ${mieter}` : ''} — ${ort} (${l.customerName})`;
}

export function LeaseQuickFill({
  onPick,
  testId = 'lease-quick-fill',
}: {
  onPick: (hit: LeaseHit | null) => void;
  testId?: string;
}) {
  const [query, setQuery] = useState('');
  // Kundentest 2026-08 (Faruq): die gewählte Nummer BLEIBT im Feld stehen —
  // vorher lief das Select unkontrolliert (value=null) und Mantine räumte die
  // Anzeige beim Verlassen des Feldes ab.
  const [gewaehlt, setGewaehlt] = useState<LeaseHit | null>(null);
  const [debounced] = useDebouncedValue(query.trim(), 200);
  const enabled = debounced.length >= 3;
  const search = useSearchControllerRun({ q: debounced }, { query: { enabled } });
  const leases = (enabled ? (search.data?.leases ?? []) : []) as LeaseHit[];

  return (
    <Select
      label="Mietvertragsnummer oder Mietername (Schnellanlage)"
      description="Nummer eintippen — Einheit, Gebäude und Rechnungsempfänger werden automatisch gefüllt."
      placeholder="Nummer oder Mietername, z. B. 97113.0018.02 oder Müller"
      searchable
      clearable
      searchValue={query}
      onSearchChange={setQuery}
      value={gewaehlt?.unitId ?? null}
      // Die getroffene Wahl bleibt in der Liste, auch wenn die Suche längst
      // andere Treffer liefert — sonst verlöre das Select seine Beschriftung.
      data={[
        ...(gewaehlt && !leases.some((l) => l.unitId === gewaehlt.unitId)
          ? [{ value: gewaehlt.unitId, label: beschriftung(gewaehlt) }]
          : []),
        ...leases.map((l) => ({ value: l.unitId, label: beschriftung(l) })),
      ]}
      nothingFoundMessage={
        enabled ? 'Keine Mietvertragsnummer gefunden.' : 'Mindestens 3 Zeichen eingeben.'
      }
      onChange={(unitId) => {
        if (!unitId) {
          setGewaehlt(null);
          onPick(null);
          return;
        }
        const hit = leases.find((l) => l.unitId === unitId) ?? gewaehlt;
        if (hit) {
          setGewaehlt(hit);
          onPick(hit);
        }
      }}
      data-testid={testId}
    />
  );
}
